import { computed, makeAutoObservable, observable, runInAction, toJS } from "mobx";
import type { RgWorld } from "src/types/RgCorePackage";
import type {
  RgBrief,
  RgBriefDto,
  RgObjectDto,
  RgZoneBrief,
  RougeDataSet,
  V3,
  RgVersions,
  RgDataSetID,
} from "@project-rouge/rg-core";
import { DefaultWorld, RgCreate, RougeDataSets } from "@project-rouge/rg-core";
import {
  getRgHash,
  getRgVersions,
  rgBriefToDto,
  rgObjectToDto,
} from "@project-rouge/rg-aggregator";
import type { Zone } from "./Zone";
import { Site } from "./Site";
import type { Building } from "./Building";
import { Dataset } from "src/constants/Dataset";
import { ArchitecturalMetrics } from "./ArchitecturalMetrics";
import objectHash from "object-hash";
import type { ZoneType } from "./Zone";
import { IsVectorOnVertex } from "src/utils/IsVectorOnVertex";
import type { ZoneBriefConfig } from "src/types/ZoneBrief";
import { ZoneBrief } from "src/types/ZoneBrief";
import type { WorldBriefConfig } from "./WorldBrief";
import { WorldBrief } from "./WorldBrief";
import type { ProjectScenarioSiteHttpPayloadForCreation } from "src/service/project/resource/project-scenario-site";
import type { GeoPoint } from "./GeoPoint";
import type { AggDesignOptionDto } from "./AggDesignOptionDto";
import { Compress, CompressAsync } from "src/utils/Compress";
import { IsVectorEqualV3 } from "src/utils/IsVectorEqual";
import { V3ToLngLat } from "src/utils/V3ToLngLat";
import { generateZoneLabel, generateZoneLabelList } from "src/utils/generateZonesNames";
import type { SavedWmsEntry } from "./stencil/wmsCatalog";

export function arePolygonEqual<T extends readonly Readonly<V3>[] | V3[]>(aPoly: T, bPoly: T) {
  if (aPoly.length !== bPoly.length) return false;
  const aPolySet = new Set(aPoly.map((point) => point.join()));
  const bPolySet = new Set(bPoly.map((point) => point.join()));
  if (aPolySet.size !== bPolySet.size) return false;
  return new Set([...aPolySet, ...bPolySet]).size === aPolySet.size;
}

export interface WorldZoneMeta {
  type: ZoneType;
  label: string;
  locked: boolean;
  offsets: number[] | null;
  brief: ZoneBriefConfig;
}

type ZonesMeta = Record<string, WorldZoneMeta>;

export interface WorldConfig {
  rgWorld?: RgWorld;
  rgVersions?: RgVersions;
  rgDatasetId?: RgDataSetID;
  label?: string;
  longitude?: number;
  latitude?: number;
  briefIndex?: number;
  brief?: Partial<WorldBriefConfig>;
  zonesMeta?: ZonesMeta;
  stencilCatalog?: SavedWmsEntry[];
}

class ExtendedPromise<T> {
  static resolve<U>(value: U | PromiseLike<U>): ExtendedPromise<U> {
    return new ExtendedPromise<U>((resolve) => {
      resolve(value);
    });
  }
  private promise: Promise<T>;
  private resolver: ((value: T | PromiseLike<T>) => void) | null = null;
  private rejecter: ((reason?: unknown) => void) | null = null;

  constructor(
    executor: (
      resolve: (value: T | PromiseLike<T>) => void,
      reject: (reason?: unknown) => void
    ) => void
  ) {
    this.promise = new Promise<T>((resolve, reject) => {
      this.resolver = resolve;
      this.rejecter = reject;
      executor(this.resolve.bind(this), this.reject.bind(this));
    });
  }

  private resolve(value: T | PromiseLike<T>): void {
    if (this.resolver) {
      this.resolver(value);
    }
  }

  private reject(reason?: unknown): void {
    if (this.rejecter) {
      this.rejecter(reason);
    }
  }

  public async getPromise(): Promise<T> {
    return this.promise;
  }

  public customResolve(value: T): void {
    this.resolve(value);
  }

  public customReject(reason?: unknown): void {
    this.reject(reason);
  }
}

export class World {
  /**
   * This function takes an array of polygons and a tolerance value, and returns a new array of
   * polygons with their vertex positions consolidated. The function merges positions that are
   * within the specified tolerance, resulting in a cleaner set of polygon vertex positions.
   */
  static sanitizeZonesOuterRing(zones: Zone[]) {
    const tolerance = 0.01;
    const positions = zones.map((polygon) => polygon.outerRing).flat();
    const list = positions
      .map((a) => {
        return positions.map((b) => {
          const key = b.join();
          let value = b;
          if (IsVectorOnVertex(a, b, tolerance)) {
            value = a;
            b[0] = a[0];
            b[1] = a[1];
            b[2] = a[2];
          }
          return [key, value] as [string, V3];
        });
      })
      .flat();

    const map = new Map(list);
    zones.forEach((zone) => {
      zone.setOuterRing(zone.outerRing.map((v) => map.get(v.join()) as V3));
    });
  }
  id: string;
  children: Site[] = [];
  briefIndex: number;
  versions: RgVersions;
  longitude: number;
  latitude: number;
  createdAt = new Date().toISOString();
  brief: WorldBrief;
  label: string;
  datasetId: RgDataSetID;
  stencilCatalog: SavedWmsEntry[] = [];

  private readonly useWorkers: boolean;
  constructor(config?: WorldConfig, useWorkers = true) {
    this.id = config?.rgWorld?.uuid ?? crypto.randomUUID();
    this.useWorkers = useWorkers;
    this.label = config?.label ?? "";
    this.briefIndex = config?.briefIndex ?? 0;
    this.versions = config?.rgVersions ?? getRgVersions();
    this.datasetId = config?.rgDatasetId ?? Dataset.id;
    this.longitude = config?.longitude ?? 0;
    this.latitude = config?.latitude ?? 0;
    this.brief = new WorldBrief(config?.brief);
    this.stencilCatalog = config?.stencilCatalog ? [...config.stencilCatalog] : [];
    config?.rgWorld?.children.forEach((rgSite) => this.addSite(new Site({ rgSite })));
    this.zones.forEach((zone) => {
      const meta = config?.zonesMeta?.[zone.id];
      if (!meta) return;
      zone.setZoneType(meta.type);
      zone.setLabel(meta.label);
      zone.setLocked(meta.locked);
      zone.setOffsets(meta.offsets);
      zone.setBrief(new ZoneBrief(zone, meta.brief));
    });
    makeAutoObservable<World>(this, {
      rgWorld: computed({ keepAlive: true }),
      rgWorldHashKey: computed({ keepAlive: true }),
      briefHashKey: computed({ keepAlive: true }),
      rgWorldDto: computed({ keepAlive: true }),
      _rgWorldZipCache: observable.shallow,
    });
  }
  get architecturalMetrics(): ArchitecturalMetrics {
    return new ArchitecturalMetrics(this.buildings);
  }
  setGeoOrigin(geoOrigin: GeoPoint) {
    this.longitude = geoOrigin.longitude;
    this.latitude = geoOrigin.latitude;
  }

  get rgVersion() {
    return toJS(this.versions);
  }

  get zonesMeta(): ZonesMeta {
    return Object.fromEntries(
      this.zones.map((zone) => [
        zone.id,
        {
          type: zone.zoneType,
          label: zone.label,
          locked: zone.locked,
          offsets: toJS(zone.offsets),
          brief: zone.brief.config,
        },
      ])
    );
  }

  get config(): Required<WorldConfig> {
    return {
      label: this.label,
      longitude: this.longitude,
      latitude: this.latitude,
      rgWorld: this.rgWorld,
      briefIndex: this.briefIndex,
      rgVersions: this.rgVersion,
      rgDatasetId: this.datasetId,
      brief: this.brief.config,
      zonesMeta: this.zonesMeta,
      stencilCatalog: toJS(this.stencilCatalog),
    };
  }

  addToStencilCatalog(entry: SavedWmsEntry) {
    if (this.stencilCatalog.some((e) => e.url === entry.url)) return;
    this.stencilCatalog = [...this.stencilCatalog, entry];
  }

  removeFromStencilCatalog(url: string) {
    this.stencilCatalog = this.stencilCatalog.filter((e) => e.url !== url);
  }

  get siteAreaHectares() {
    const sumArray = (arr: number[]) => arr.reduce((sum, value) => sum + value, 0);

    const areas = this.zones.map((zone) => zone.siteAreaHectares) ?? [0];
    return sumArray(areas);
  }

  get centroid(): V3 {
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    let totalVertices = 0;

    for (const site of this.sites) {
      for (const point of site.outerRing) {
        sumX += point[0];
        sumY += point[1];
        sumZ += point[2];
        totalVertices++;
      }
    }

    const centroidX = sumX / totalVertices;
    const centroidY = sumY / totalVertices;
    const centroidZ = sumZ / totalVertices;

    return [centroidX, centroidY, centroidZ];
  }

  get dataset(): RougeDataSet {
    return RougeDataSets[this.datasetId];
  }

  get rgBrief(): RgBrief {
    const zoneBriefs: Record<string, RgZoneBrief> = {};
    for (const zone of this.zones) {
      if (!zone.rgZoneBrief) continue;
      zoneBriefs[zone.id] = zone.rgZoneBrief;
    }
    return {
      obj: toJS(this.rgWorld),
      dataset: this.dataset,
      zoneBriefs,
      face2FaceSeparation: this.brief.buildingSeparation,
      face2SideSeparation: 0,
    };
  }

  get rgBriefDto(): RgBriefDto {
    const dto = rgBriefToDto(this.rgBrief);
    return dto;
  }

  /** only zone structural changes and brief are included in the hash key. Used by generator */
  get zonesBriefsHashKey(): string {
    const key = this.zones.map((z) => [toJS(z.polygons), z.brief.config, z.locked, z.zoneType]);
    return objectHash([key, this.brief.config]);
  }

  get briefHashKey(): string {
    return objectHash(this.brief);
  }

  get zones(): Zone[] {
    return this.sites.flatMap((site) => site.zones);
  }
  get sites(): Site[] {
    return this.children;
  }
  get hasSite(): boolean {
    return this.sites.length > 0;
  }
  get hasZone(): boolean {
    return this.zones.length > 0;
  }
  get buildings(): Building[] {
    return this.zones.flatMap((zone) => zone.buildings);
  }
  get hasBuilding(): boolean {
    return this.buildings.length > 0;
  }
  get geoOrigin(): GeoPoint {
    return { longitude: this.longitude, latitude: this.latitude };
  }

  get rgWorldHashKey() {
    return getRgHash(this.rgWorld);
  }

  _rgWorldZipCache = {
    hashKey: "",
    zip: null as Uint8Array | null,
    promise: null as ExtendedPromise<Uint8Array> | null,
  };

  async getRgWorldZip(): Promise<Uint8Array> {
    this.invalidateCache();
    if (this._rgWorldZipCache.promise) return this._rgWorldZipCache.promise.getPromise();
    this._rgWorldZipCache.promise = new ExtendedPromise(async (resolve) => {
      const zip = this.useWorkers
        ? await CompressAsync(this.rgWorldDto)
        : Compress(this.rgWorldDto);
      runInAction(() => {
        this._rgWorldZipCache.zip = zip;
      });
      resolve(zip);
    });
    return this._rgWorldZipCache.promise.getPromise();
  }

  get hashKey(): string {
    return `${this.rgWorldHashKey}_${this.briefHashKey}`;
  }

  get rgWorldDto(): RgObjectDto<RgWorld> {
    return rgObjectToDto(this.rgWorld);
  }

  get rgWorld(): RgWorld {
    const world: RgWorld = {
      ...RgCreate<RgWorld>(DefaultWorld, this.dataset.rvData),
      uuid: this.id,
      data: {
        coords: { latitude: 0, longitude: 0 },
        dataset: this.dataset.id,
      },
      children: this.sites.map((site) => site.rgSite),
    };
    for (const child of world.children) {
      child.parent = world;
    }
    return world;
  }

  get beBrief() {
    return this.brief.beBrief;
  }

  get beSites(): ProjectScenarioSiteHttpPayloadForCreation[] {
    return this.sites.map((site) => site.beSite);
  }

  get aggDesignOptionDto(): AggDesignOptionDto {
    return {
      aggProps: null as never,
      briefDto: {
        ...this.rgBriefDto,
        obj: null,
      },
      briefIndex: this.briefIndex,
      id: this.id,
      obj: this.rgWorldDto,
      versions: this.versions,
    };
  }

  setLabel(label: string) {
    this.label = label;
  }

  clone(): World {
    const world = new World(this.config);
    // world.setRgWorldZip(this.rgWorldZip);
    return world;
  }

  setId(id: string) {
    this.id = id;
  }

  addSite(site: Site) {
    if (this.children.includes(site)) return;
    site.parent = this;
    this.children.push(site);
  }

  removeSite(site: Site) {
    this.children = this.sites.filter((s) => s !== site);
    site.parent = undefined;
  }

  addZones(zones: Zone[]) {
    zones.forEach((zone) => this.addZone(zone));
    const firstZone = zones[0];
    if (!IsVectorEqualV3(firstZone.outerRing[0], [0, 0, 0])) {
      const firstPoint = firstZone.outerRing[0];
      const [longitude, latitude] = V3ToLngLat(firstPoint, [
        this.geoOrigin.longitude,
        this.geoOrigin.latitude,
      ]);
      this.setGeoOrigin({ longitude, latitude });
      const offset: V3 = [-firstPoint[0], firstPoint[1], -firstPoint[2]];
      zones.forEach((zone) => zone.moveBy(offset));
    }
  }

  /** Add a zone and create site if necessary. We also going to assign a new label if a label is empty */
  addZone(zone: Zone) {
    if (this.zones.includes(zone)) return;
    const zoneId = zone.id;
    const existingZone = this.zones.find((zone) => zone.id === zoneId);
    if (existingZone) return;
    const site = new Site();
    site.addZone(zone);
    this.addSite(site);
    if (!zone.label) {
      zone.setLabel(generateZoneLabel(this.zones.map((z) => z.label)));
    }
  }

  replaceZones(zones: Zone[]) {
    this.sites.forEach((site) => this.removeSite(site));
    const labels = generateZoneLabelList(zones.map((z) => z.label));
    zones.forEach((zone, i) => zone.setLabel(labels[i]));
    this.addZones(zones);
  }
  removeZone(zone: Zone) {
    const sites = [...this.sites];
    sites.forEach((site) => {
      site.removeZone(zone);
      if (!site.hasZone) this.removeSite(site);
    });
  }
  removeBuilding(buildings: Building | Building[]) {
    if (!Array.isArray(buildings)) buildings = [buildings];
    this.zones.forEach((zone) => zone.removeBuilding(buildings));
  }

  /** @todo this could cause memory leak if there is already a listener for current promise */
  setRgWorldZip(zip: Uint8Array | null) {
    this._rgWorldZipCache = {
      hashKey: this.hashKey,
      zip,
      promise: zip ? ExtendedPromise.resolve(zip) : null,
    };
  }

  private invalidateCache() {
    if (this.rgWorldHashKey === this._rgWorldZipCache.hashKey) return;
    this._rgWorldZipCache.promise?.customReject();
    this._rgWorldZipCache = {
      hashKey: "",
      zip: null,
      promise: null,
    };
  }
}
